import React from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuIcon from "@mui/icons-material/Menu";
import { useTheme } from "@mui/material/styles";
import Menu from "@mui/material/Menu";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { useIonViewDidEnter, useIonViewDidLeave } from "@ionic/react";
import { isVerboseLoggingEnabled } from "../../utils/debug";
import { useMobileTopSpacerColor } from "../../hooks/useMobileTopSpacerColor";

type MenuItem = {
  key?: string | number;
  label: React.ReactNode;
  onClick?: () => void;
};

type MobileAppBarProps = {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  subtitleNode?: React.ReactNode;
  onBack?: () => void;
  actions?: React.ReactNode;
  backgroundColor?: string;
  textColor?: string;
  elevation?: number;
  menuItems?: MenuItem[];
  position?: 'fixed' | 'static';
  rightChip?: React.ReactNode;
};

const MobileAppBar: React.FC<MobileAppBarProps> = ({
  title,
  subtitle,
  subtitleNode,
  onBack,
  actions,
  backgroundColor,
  textColor,
  elevation = 0,
  menuItems = [],
  position = 'fixed',
  rightChip,
}) => {
  const theme = useTheme();
  const verbose = isVerboseLoggingEnabled();

  const fg =
    textColor ??
    (theme as any)?.background ??
    theme.palette.primary.contrastText;
  const bg =
    backgroundColor ?? (theme as any)?.brand ?? theme.palette.primary.main;

  const appBarRef = React.useRef<HTMLDivElement | null>(null);
  const toolbarRef = React.useRef<HTMLDivElement | null>(null);
  const hamburgerButtonRef = React.useRef<HTMLButtonElement | null>(null);

  const [menuOpen, setMenuOpen] = React.useState(false);
  const hasMenuItems = menuItems.length > 0;
  // Update a CSS variable with the current app bar/toolbar height when using a fixed header
  React.useEffect(() => {
    const updateOffset = () => {
      try {
        if (position !== 'fixed') return;
        const h = toolbarRef.current?.offsetHeight ?? 64;
        document.documentElement.style.setProperty('--appbar-offset', `${h}px`);
      } catch (_) {
        // ignore
      }
    };
    updateOffset();
    window.addEventListener('resize', updateOffset);
    return () => window.removeEventListener('resize', updateOffset);
  }, [position]);

  const [isViewActive, setIsViewActive] = React.useState(false);

  useIonViewDidEnter(() => {
    setIsViewActive(true);
  });

  useIonViewDidLeave(() => {
    setIsViewActive(false);
  });

  useMobileTopSpacerColor(isViewActive ? bg : undefined);

  React.useEffect(() => {
    if (!verbose) {
      return;
    }
    try {
      const appBarNode = appBarRef.current as HTMLElement | null;
      const toolbarNode = toolbarRef.current as HTMLElement | null;
      const btnNode = hamburgerButtonRef.current as HTMLElement | null;
      const getInfo = (el: HTMLElement | null) => {
        if (!el) return null;
        const rect = el.getBoundingClientRect();
        const styles = window.getComputedStyle(el);
        return {
          rect: { x: rect.x, y: rect.y, w: rect.width, h: rect.height },
          zIndex: styles.zIndex,
          position: styles.position,
          visibility: styles.visibility,
          display: styles.display,
          pointerEvents: styles.pointerEvents,
        };
      };
      // eslint-disable-next-line no-console
      console.log('[MobileAppBar DEBUG] layout', {
        appBar: getInfo(appBarNode!),
        toolbar: getInfo(toolbarNode!),
        hamburger: getInfo(btnNode!),
      });
    } catch (_error) {
      // ignore
    }
  }, [verbose, position]);

  if (verbose) {
    console.log('[MobileAppBar] colors', { fg, bg });
  }

  return (
    <>
      <AppBar
        ref={appBarRef}
        position={position}
        color="transparent"
        elevation={elevation}
        sx={{
          backgroundColor: bg,
          zIndex: (theme) => (position === 'fixed' ? (((theme as any).zIndex?.modal ?? 1300) + 100) : undefined),
        }}
        style={{ borderRadius: 0 }}
      >
        <Toolbar
          ref={toolbarRef}
          sx={{ minHeight: 64, px: 2, position: "relative", justifyContent: 'space-between' }}
        >
          {onBack ? (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="back"
              onClick={onBack}
              sx={{ color: fg, mr: 1 }}
            >
              <ArrowBackIcon />
            </IconButton>
          ) : null}

          <Box
            sx={{
              flex: '1 1 auto',
              minWidth: 0,
              overflow: 'hidden',
              color: fg,
              fontFamily: 'inherit',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
              <Typography
                variant="h5"
                noWrap
                sx={{
                  color: fg,
                  padding: '1rem 0 .25rem 0',
                  fontWeight: 600,
                  lineHeight: 1.25,
                  fontFamily: 'inherit',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  // Smoothly shrink on narrow screens to avoid crowding the hamburger
                  fontSize: 'clamp(1.125rem, 4.2vw, 1.5rem)',
                  flex: '1 1 auto',
                }}
              >
                {title}
              </Typography>
              {rightChip && (
                <Box sx={{ ml: 1, flexShrink: 0 }}>{rightChip}</Box>
              )}
              {hasMenuItems ? (
                <IconButton
                  aria-label="app-menu"
                  onClick={() => setMenuOpen(true)}
                  size="large"
                  ref={hamburgerButtonRef}
                  sx={{
                    color: '#fff',
                    alignSelf: 'flex-start',
                    mt: '0.5rem',
                  }}
                  aria-controls={menuOpen ? "mobile-appbar-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={menuOpen ? "true" : undefined}
                >
                  <MenuIcon sx={{ fontSize: { xs: 24, sm: 28 }, color: '#fff' }} />
                </IconButton>
              ) : null}
            </Box>
            {subtitleNode ? (
              <Box
                sx={{
                  color: fg,
                  opacity: 0.9,
                  px: 1,
                  pb: 1,
                  fontFamily: 'inherit',
                }}
              >
                {subtitleNode}
              </Box>
            ) : (
              <Typography
                variant="h6"
                sx={{
                  color: fg,
                  opacity: 0.7,
                  padding: '0 .5rem .75rem .5rem',
                  whiteSpace: 'pre-line',
                  fontWeight: 500,
                  lineHeight: 1.3,
                  fontFamily: 'inherit',
                  // Slightly responsive subtitle as well
                  fontSize: 'clamp(0.95rem, 3.7vw, 1.125rem)',
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>

          {actions ? <Box sx={{ ml: 1 }}>{actions}</Box> : null}

          {verbose
            ? (() => {
                console.log(
                  '[MobileAppBar] menuItems length',
                  Array.isArray(menuItems) ? menuItems.length : 'n/a'
                );
                return null;
              })()
            : null}
          

          <Menu
            id="mobile-appbar-menu"
            anchorEl={toolbarRef.current}
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
            style={{ transform: `translateX(-1rem)` }}
            keepMounted
            slotProps={{
              paper: {
                sx: {
                  width: "calc(100vw + 2rem)",
                  maxWidth: "calc(100vw + 2rem)",
                  boxShadow: theme.shadows[4],
                  borderRadius: 0,
                  margin: 0,
                },
              },
            }}
          >
            <Box sx={{ width: "100%" }}>
              <List disablePadding>
                {menuItems.map((it, i) => (
                  <ListItem
                    key={it.key ?? i}
                    onClick={() => {
                      setMenuOpen(false);
                      it.onClick && it.onClick();
                    }}
                  >
                    <ListItemText primary={it.label} />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Menu>
        </Toolbar>
      </AppBar>
    </>
  );
};

export default MobileAppBar;
